import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  getUserRole,
  getUserDetails,
} from "../../../backend/auth";
import {
  DEFAULT_OPTIONS_PAYLOAD,
} from "../../common/Table";
import {
  useGetVendorHeirarchyRecords,
} from "../../../backend/masters";
import downloadButton from "./../../../assets/img/icon/downloadButton.svg";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useExportSummaryAuditReport } from "../../../backend/exports";
import { toast } from "react-toastify";

const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

const ALL_OPTION = {
  value: "all",
  label: "ALL"
};

function extractCategories(companiesList: any[]) {
  const map = new Map<any, any>();
  (companiesList || []).forEach((c: any) => {
    (c.associate_companies || []).forEach((ac: any) => {
      (ac.locations || []).forEach((loc: any) => {
        (loc.categories || []).forEach((cat: any) => {
          if (!map.has(cat.id)) {
            map.set(cat.id, { ...cat, vendors: [...(cat.vendors || [])] });
          } else {
            const existing = map.get(cat.id);
            const existingIds = new Set(existing.vendors.map((v: any) => v.id));
            (cat.vendors || []).forEach((v: any) => {
              if (!existingIds.has(v.id)) { existing.vendors.push(v); existingIds.add(v.id); }
            });
          }
        });
      });
    });
  });
  return Array.from(map.values());
}

function extractVendors(categoriesList: any[]) {
  const seen = new Set();
  const result: any[] = [];
  (categoriesList || []).forEach((cat: any) => {
    (cat.vendors || []).forEach((v: any) => {
      if (!seen.has(v.id)) { seen.add(v.id); result.push(v); }
    });
  });
  return result;
}

function VendorAuditDashboardFilter({ onChange }: any) {
  const _role = getUserRole();
  const user = getUserDetails();
  const [t] = useState(new Date().getTime());
  const [company, setCompany] = useState<any>(ALL_OPTION);
  const [associate_company, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [vendorCategoriesId, setVendorCategoriesId] = useState<any>(ALL_OPTION);
  const [vendorRegistrationId, setVendorRegistrationId] = useState<any>(ALL_OPTION);
  const [month, setMonth] = useState<any>(ALL_OPTION);
  const [year, setYear] = useState<any>(null);

  // State for hierarchical data
  const [associateCompanies, setAssociateCompanies] = useState<any>(null);
  const [locations, setLocations] = useState<any>(null);
  const [categories, setCategories] = useState<any>(null);
  const [vendors, setVendors] = useState<any>(null);

  // Year options - Current year and past 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));

  // User-specific API call - gets hierarchical data
  const { companies: parentCompanies, isFetching: fetchingCompanies } =
    useGetVendorHeirarchyRecords({
      filters: {
        user_id_eq: user.user_id,
        is_active_eq: true
      }
    });

  const { exportSummaryAuditReport, exporting } = useExportSummaryAuditReport(
    (response: any) => {
      toast.success(response.message);
    },
    (error: any) => {
      const errorMessage = (error && error.response && error.response.data && error.response.data.message);
      toast.error(errorMessage);
    }
  );

  // When company changes: update associate companies, categories, vendors
  useEffect(() => {
    setAssociateCompany(ALL_OPTION);
    setLocations(null);
    setLocation(ALL_OPTION);
    setVendorCategoriesId(ALL_OPTION);
    setVendorRegistrationId(ALL_OPTION);

    if (company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      if (_company) {
        setAssociateCompanies(_company.associate_companies);
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else {
      setAssociateCompanies(null);
      if (parentCompanies) {
        const cats = extractCategories(parentCompanies);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [company]);

  // When associate company changes: update locations, categories, vendors
  useEffect(() => {
    setLocations(null);
    setLocation(ALL_OPTION);
    setVendorCategoriesId(ALL_OPTION);
    setVendorRegistrationId(ALL_OPTION);

    if (associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      if (_ac) {
        setLocations(_ac.locations);
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      if (_company) {
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [associate_company]);

  // When location changes: update categories, vendors
  useEffect(() => {
    setVendorCategoriesId(ALL_OPTION);
    setVendorRegistrationId(ALL_OPTION);

    if (location && location.value !== "all" && associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      const _loc = _ac && _ac.locations
        ? _ac.locations.find((c: any) => c.id == location.value)
        : null;
      if (_loc) {
        const cats = extractCategories([{ associate_companies: [{ locations: [_loc] }] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      if (_ac) {
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [location]);

  // When vendor category changes: update vendors
  useEffect(() => {
    setVendorRegistrationId(ALL_OPTION);

    if (vendorCategoriesId && vendorCategoriesId.value !== "all") {
      const _cat = (categories || []).find((c: any) => c.id == vendorCategoriesId.value);
      if (_cat) {
        setVendors(_cat.vendors || []);
      }
    } else {
      setVendors(extractVendors(categories || []));
    }
  }, [vendorCategoriesId]);

  // When companies first load, set initial categories and vendors
  useEffect(() => {
    if (parentCompanies && parentCompanies.length > 0 && !fetchingCompanies) {
      const cats = extractCategories(parentCompanies);
      setCategories(cats);
      setVendors(extractVendors(cats));
    }
  }, [parentCompanies]);

  const handleChangeMonth = (selectedMonth: any) => {
    setMonth(selectedMonth);
  };

  const handleChangeYear = (selectedYear: any) => {
    setYear(selectedYear);
  };

  // Auto-select current year on load
  useEffect(() => {
    if (!year) {
      const currentYear = new Date().getFullYear();
      const yearOption = yearOptions.find(opt => opt.value === currentYear);
      if (yearOption) {
        setYear(yearOption);
      }
    }
  }, []);

  // Trigger onChange when filters change
  useEffect(() => {
    if (!parentCompanies || parentCompanies.length === 0) {
      return;
    }

    const filters: any = {};

    if (company && company.value === "all") {
      const allCompanyIds = (parentCompanies || []).map((c: any) => c.id);
      if (allCompanyIds.length > 0) {
        filters.all_company_ids = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company = company.value;
    }

    if (associate_company && associate_company.value !== "all") {
      filters.associate_company = associate_company.value;
    }

    if (location && location.value !== "all") {
      filters.location_id = location.value;
    }

    if (vendorCategoriesId && vendorCategoriesId.value !== "all") {
      filters.vendor_category_id = vendorCategoriesId.value;
    }

    if (vendorRegistrationId && vendorRegistrationId.value !== "all") {
      filters.vendor_registration_id = vendorRegistrationId.value;
    }

    filters.month = month && month.value !== "all" ? month.value : null;
    filters.year = year ? year.value : null;

    onChange(filters);
  }, [company, associate_company, location, vendorCategoriesId, vendorRegistrationId, month, year, parentCompanies]);
  
  const isDownloadEnabled = () => {
      return !!(company && company.value !== "all" && associate_company && associate_company.value !== "all" && month && month.value !== "all" && year);
    };

    // Get missing required fields for tooltip
    const getMissingFields = () => {
      const missing: string[] = [];
      if (!company || company.value === "all") missing.push('Company');
      if (!associate_company || associate_company.value === "all") missing.push('Associate Company');
      if (!month || month.value === "all") missing.push('Month');
      if (!year) missing.push('Year');
      return missing;
    };

    // Format month_year as "MonthName Year"
    const formatMonthYear = () => {
      if (!month || !year) return '';
      const monthOption = monthOptions.find(m => m.value === month.value);
      const monthName = monthOption ? monthOption.label : '';
      return `${monthName} ${year.value}`;
    };

    // Handle download button click
    const handleDownloadClick = () => {
      if (!isDownloadEnabled()) {
        return; // Button is disabled, do nothing
      }

      // Build payload
      const payload: any = {
        company_id: company.value,
        associate_company_id: associate_company.value,
        month_year: formatMonthYear()
      };

      // Add optional filters if selected
      if (location && location.value !== "all") {
        payload.location_ids = [location.value];
      }
      if (vendorCategoriesId && vendorCategoriesId.value !== "all") {
        payload.vendor_category_ids = [vendorCategoriesId.value];
      }
      if (vendorRegistrationId && vendorRegistrationId.value !== "all") {
        payload.vendor_registration_ids = [vendorRegistrationId.value];
      }

      // Call export API
      exportSummaryAuditReport(payload);
    };

    // Get tooltip message
    const getTooltipMessage = () => {
      const missing = getMissingFields();
      if (missing.length === 0) return 'Download Report';
      return `Select ${missing.join(', ')} first`;
    };

  function selectVendor(selected: any) {
    if (selected.value === vendorRegistrationId.value) {
      return;
    } else {
      setVendorRegistrationId(selected);
    }
  }

  return (
    <>
      <div className="flex-grow-1">
        <label className="filter-label">
          Company
        </label>
        <Select
          placeholder="Company"
          options={[
            ALL_OPTION,
            ...(parentCompanies || []).map((x: any) => ({
              value: x.id, label: x.name
            }))
          ]}
          onChange={setCompany}
          value={company}
          className="select-control"
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Associate Company
        </label>
        <Select
          placeholder="Associate Company"
          options={
            Array.isArray(associateCompanies)
              ? [
                  ALL_OPTION,
                  ...associateCompanies.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setAssociateCompany}
          value={associate_company}
          className="select-control"
          isDisabled={!company || company.value === "all"}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Location
        </label>
        <Select
          placeholder="Location"
          options={
            Array.isArray(locations)
              ? [
                  ALL_OPTION,
                  ...locations.map((loc: any) => ({
                    value: loc.id,
                    label: loc.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setLocation}
          value={location}
          className="select-control"
          isDisabled={!associate_company || associate_company.value === "all"}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Vendor Category
        </label>
        <Select
          placeholder="Category"
          options={
            Array.isArray(categories)
              ? [
                  ALL_OPTION,
                  ...categories.map((x: any) => ({
                    value: x.id, label: x.name
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setVendorCategoriesId}
          value={vendorCategoriesId}
          className="select-control"
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Vendor
        </label>
        <Select
          placeholder="Vendors"
          options={
            Array.isArray(vendors)
              ? [
                  ALL_OPTION,
                  ...vendors.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={selectVendor}
          value={vendorRegistrationId}
          className="select-control"
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Month
        </label>
        <Select
          options={[ALL_OPTION, ...monthOptions]}
          onChange={handleChangeMonth}
          value={month}
          className="select-control"
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Year
        </label>
        <Select
          placeholder="Select Year"
          options={yearOptions}
          onChange={handleChangeYear}
          value={year}
          className="select-control"
        />
      </div>
      <div style={{ alignSelf: "flex-end" }}>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="download-tooltip">
                {getTooltipMessage()}
              </Tooltip>
            }
          >
            <div
              className={`border d-flex align-items-center justify-content-center ${isDownloadEnabled() ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              style={{
                borderRadius: "8px",
                opacity: isDownloadEnabled() ? 1 : 0.4,
                transition: "opacity 0.2s",
                height: "38px",
                width: "38px",
              }}
              onClick={handleDownloadClick}
            >
              <img
                className="d-block"
                src={downloadButton}
                alt="downloadButton"
                style={{ pointerEvents: 'none' }}
              />
            </div>
          </OverlayTrigger>
        </div>
    </>
  );
}

export default VendorAuditDashboardFilter;
